import React from 'react'
import { useQuery } from 'react-apollo'
import gql from 'graphql-tag'
import {
  PrimaryNavLink,
  LocalizedText,
  TableContext,
  FilterableTable,
  useHandler,
  Tooltip,
  Icon,
  isFilterInSentence,
} from 'labguard-cdk'
import { PageOptionsPortal, CreateIcon, AlarmWinkingChip } from 'src/components/common'
import { useAppPermission } from 'src/hooks'
import { AlarmIcon, EyeIcon } from 'src/components/common/icons'
import { useIntl } from 'react-intl'
import GroupsPrintButton from '../GroupsPrintButton'
import GroupActions from './GroupActions'
import GroupVisibility from './GroupVisibility'

export const query = gql`
  query groupListView {
    monitoredGroups {
      id
      name
      hidden
      channels {
        id
      }
      criticalities
      nbChannelsWithAlarm
      winkingAlarm {
        id
        toBeAcknowledged
      }
      colors @client
    }
  }
`

const defaultColumns = [
  {
    dataKey: 'nbChannelsWithAlarm',
    width: 50,
    flexGrow: 0,
    flexShrink: 0,
    label: (
      <Tooltip title={<LocalizedText id="WEB_ALARMS" />}>
        <AlarmIcon />
      </Tooltip>
    ),
    overflow: true,
    align: 'center',
    // eslint-disable-next-line react/prop-types, react/display-name
    cellRenderer: ({ rowData, cellData }) => (
      // eslint-disable-next-line react/prop-types
      <AlarmWinkingChip colors={rowData.colors} winkingAlarm={rowData.winkingAlarm} nbAlarms={cellData} />
    ),
  },
  {
    dataKey: 'name',
    width: 100,
    flexGrow: 1,
    label: <LocalizedText id="WEB_NAME" />,
  },
  {
    dataKey: 'nbChannels',
    cellDataGetter: ({ rowData }) => rowData.channels.length,
    width: 100,
    align: 'right',
    flexGrow: 1,
    label: <LocalizedText id="WEB_CHANNELS_COUNT" />,
  },
  {
    width: 100,
    flexGrow: 0,
    flexShrink: 0,
    label: <LocalizedText id="WEB_ACTIONS" />,
    dataKey: 'actions',
    disableSort: true,
    align: 'center',
    overflow: true,
    // eslint-disable-next-line react/display-name,react/prop-types
    cellRenderer: ({ rowData }) => <GroupActions group={rowData} />,
  },
]

const visibilityColumn = {
  dataKey: 'visibility',
  width: 50,
  flexGrow: 0,
  flexShrink: 0,
  label: (
    <Tooltip title={<LocalizedText id="WEB_HIDE_MY_MONITORED_GROUPS" />}>
      <Icon icon={EyeIcon} />
    </Tooltip>
  ),
  overflow: true,
  align: 'center',
  cellDataGetter: ({ rowData }) => rowData.hidden,
  // eslint-disable-next-line react/prop-types, react/display-name
  cellRenderer: ({ rowData }) => (
    // eslint-disable-next-line react/prop-types
    <GroupVisibility group={rowData} />
  ),
}

const predicate = ({ filter, rowData }) => isFilterInSentence(filter, rowData.name)

const GroupListView = () => {
  const enableCreate = useAppPermission('groups.create')
  const enableToggle = useAppPermission('groups.toggle')

  const columns = React.useMemo(() => (enableToggle ? [visibilityColumn, ...defaultColumns] : defaultColumns), [
    enableToggle,
  ])

  const { loading, data: { monitoredGroups = [] } = {} } = useQuery(query)
  const { useData, sort, sortBy, sortDirection } = TableContext.use()
  const data = useData(monitoredGroups)

  const getIds = useHandler(() => data.map(({ id }) => id))
  const navLinkTo = useHandler(({ rowData }) => rowData.id)

  const intl = useIntl()

  return (
    <React.Fragment>
      <PageOptionsPortal justify="space-between">
        <span>
          {enableCreate && (
            <PrimaryNavLink icon={CreateIcon} to="create">
              <LocalizedText id="WEB_ADD_GROUP" />
            </PrimaryNavLink>
          )}
        </span>
        <GroupsPrintButton getIds={getIds} />
      </PageOptionsPortal>
      <FilterableTable
        loading={loading}
        data={data}
        columns={columns}
        navLinkTo={navLinkTo}
        sort={sort}
        sortBy={sortBy}
        sortDirection={sortDirection}
        predicate={predicate}
        inputProps={{
          label: <LocalizedText id="WEB_FILTER_BY_NAME" />,
          placeholder: intl.formatMessage({ id: 'WEB_GROUP_NAME' }),
        }}
      />
    </React.Fragment>
  )
}

export default GroupListView
